import { Component } from '@angular/core';
import { ProfileComponent } from '../profile/profile.component';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute } from '@angular/router';
import { RouterService } from '../../services/router.service';
import { User } from '../../model/user';
import { Friendship, FriendshipStatus, CreateFriendshipPayload } from '../../model/friends';
import { CourtReservation } from '../../model/court-reservation';
import { FriendsService } from '../../services/friends.service';

@Component({
  selector: 'app-available-players',
  standalone: true,
  imports: [ProfileComponent],
  templateUrl: './available-players.component.html',
  styleUrl: './available-players.component.css'
})
export class AvailablePlayersComponent {

  currentUser: User;
  currentUserId: number;
  userId: number = 0;
  idx: number = 0;
  courtReservation: CourtReservation;
  friendRequestSent: boolean = false;
  private friendships: Friendship[] = [];

  constructor(
    private localStorageService: LocalStorageService,
    private route: ActivatedRoute,
    private routerService: RouterService,
    private friendsService: FriendsService,
  ) {
    this.currentUser =
      (this.safeGetItem<User>('currentUser') as User) ||
      ({ id: 0, username: '', name: '', last_name: '' } as unknown as User);
    this.currentUserId = this.normalizeId(this.currentUser?.id);
    this.currentUser = { ...this.currentUser, id: this.currentUserId } as User;
    this.courtReservation =
      (this.safeGetItem<CourtReservation>('courtReservation') as CourtReservation) ||
      ({ player_ids: [-1, -1, -1, -1] } as CourtReservation);
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params  => {
      this.userId = Number(params.get('id')) || 0;
      this.idx = Number(params.get('idx')) || this.idx;
      this.updateFriendRequestStatus();
    });

    if (this.currentUserId) {
      this.loadFriendships();
    }
  }

  private loadFriendships(): void {
    this.friendsService.getFriendshipsForUser(this.currentUserId).subscribe({
      next: (friendships: Friendship[]) => {
        this.friendships = friendships || [];
        this.updateFriendRequestStatus();
      },
      error: (error) => {
        console.error('Failed to load friendships', error);
        this.friendships = [];
        this.friendRequestSent = false;
      },
    });
  }

  private updateFriendRequestStatus(): void {
    if (!this.userId) {
      this.friendRequestSent = false;
      return;
    }
    const existing = this.friendships.find((friendship) =>
      this.matchesFriendship(friendship, this.userId)
    );

    if (!existing) {
      this.friendRequestSent = false;
      return;
    }

    this.friendRequestSent = existing.status !== FriendshipStatus.Denied;
  }

  private matchesFriendship(friendship: Friendship, userId: number): boolean {
    return (
      (friendship.user_id === this.currentUserId && friendship.friend_id === userId) ||
      (friendship.friend_id === this.currentUserId && friendship.user_id === userId)
    );
  }

  private isFriendOrPending(userId: number): boolean {
    return this.friendships.some((friendship) => {
      if (!this.matchesFriendship(friendship, userId)) {
        return false;
      }
      return friendship.status !== FriendshipStatus.Denied;
    });
  }

  private safeGetItem<T>(key: string): T | null {
    try {
      return this.localStorageService.getItem(key) as T;
    } catch (error) {
      return null;
    }
  }

  private normalizeId(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  invitePlayer() {
    const i = this.userId;

    if (this.idx < this.courtReservation.player_ids.length && this.idx != -1) {
      this.courtReservation.player_ids[this.idx] = i;
      this.localStorageService.setItem('courtReservation', this.courtReservation);
      if (this.idx + 1 < this.courtReservation.player_ids.length &&
            this.courtReservation.player_ids[this.idx + 1] == -1) {
              this.idx = this.idx + 1;
      } else {
        this.idx = -1;
      }
      this.routerService.navigateTo('confirmation/invite-player');
    }
  }

  addFriend() {
    const i = this.userId;

    if (!i || !this.currentUserId) {
      return;
    }

    if (this.isFriendOrPending(i)) {
      this.friendRequestSent = true;
      return;
    }

    const payload: CreateFriendshipPayload = {
      user_id: this.currentUserId,
      friend_id: i,
      status: FriendshipStatus.Pending,
      created_at: new Date().toISOString(),
    };

    this.friendsService.createFriendship(payload).subscribe({
      next: () => {
        this.friendRequestSent = true;
        this.loadFriendships();
        this.routerService.navigateTo('confirmation/add-friend');
      },
      error: (error) => {
        console.error('Failed to send friend request', error);
      }
    });
  }
}
